import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { api } from "../../api/client";
import type { ClassFormat } from "../../api/hooks";
import { LoadingInline } from "../Spinner";

type FormatDraft = {
  code: string;
  name: string;
  duration: number;
  maxCapacity: number;
  description: string;
  color: string;
};

const EMPTY_DRAFT: FormatDraft = {
  code: "",
  name: "",
  duration: 60,
  maxCapacity: 15,
  description: "",
  color: "#DC2626",
};

const HEX_RE = /^#[0-9A-Fa-f]{6}$/;

export function FormatsManager() {
  const qc = useQueryClient();
  const { data: formats, isLoading } = useQuery({
    queryKey: ["admin", "formats"],
    queryFn: () => api<ClassFormat[]>("/admin/formats"),
  });

  const [open, setOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [draft, setDraft] = useState<FormatDraft>(EMPTY_DRAFT);
  const isEditing = editingId !== null;

  const resetForm = () => {
    setEditingId(null);
    setDraft(EMPTY_DRAFT);
  };

  const startEdit = (f: ClassFormat) => {
    setEditingId(f.id);
    setDraft({
      code: f.code,
      name: f.name,
      duration: f.duration,
      maxCapacity: f.maxCapacity,
      description: f.description ?? "",
      color: f.color ?? "#DC2626",
    });
  };

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ["admin", "formats"] });
    qc.invalidateQueries({ queryKey: ["admin", "schedule"] });
    qc.invalidateQueries({ queryKey: ["schedule"] });
    qc.invalidateQueries({ queryKey: ["formats"] });
  };

  const save = useMutation({
    mutationFn: () => {
      const body = {
        code: draft.code.trim(),
        name: draft.name.trim(),
        duration: draft.duration,
        maxCapacity: draft.maxCapacity,
        description: draft.description,
        color: draft.color,
      };
      return editingId
        ? api(`/admin/formats/${editingId}`, { method: "PUT", json: body })
        : api("/admin/formats", { method: "POST", json: body });
    },
    onSuccess: () => {
      invalidate();
      resetForm();
    },
    onError: (err: Error) => window.alert(err.message),
  });

  const remove = useMutation({
    mutationFn: (id: string) => api(`/admin/formats/${id}`, { method: "DELETE" }),
    onSuccess: (_data, id) => {
      invalidate();
      if (editingId === id) resetForm();
    },
    onError: (err: Error) => window.alert(err.message),
  });

  const handleDelete = (f: ClassFormat) => {
    if (window.confirm(`Supprimer le type de créneau « ${f.name} » ?`)) remove.mutate(f.id);
  };

  const codeValid = draft.code.trim().length > 0;
  const nameValid = draft.name.trim().length > 0;
  const colorValid = HEX_RE.test(draft.color);
  const canSave =
    codeValid && nameValid && colorValid && draft.duration > 0 && draft.maxCapacity > 0;

  return (
    <div className="card mt-6">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex w-full items-center justify-between"
      >
        <h2 className="font-display text-lg font-bold">Types de créneaux</h2>
        <span className="text-sm text-ink-500">{open ? "▲ Masquer" : "▼ Gérer"}</span>
      </button>

      {open && (
        <div className="mt-4">
          {isLoading && <LoadingInline label="Chargement des formats…" />}

          {/* Liste des formats existants */}
          <div className="grid gap-2 sm:grid-cols-2">
            {formats?.map((f) => {
              const isCurrent = editingId === f.id;
              return (
                <div
                  key={f.id}
                  className={`flex items-center gap-3 rounded border p-2 ${
                    isCurrent ? "border-brand-500 bg-brand-50" : "border-ink-200 bg-white"
                  }`}
                >
                  <span
                    className="inline-block h-4 w-4 shrink-0 rounded-full border border-ink-300"
                    style={{ backgroundColor: f.color ?? "#DC2626" }}
                  />
                  <div className="min-w-0 flex-1">
                    <div className="truncate text-sm font-semibold">{f.name}</div>
                    <div className="truncate text-xs text-ink-500">
                      {f.code} · {f.duration} min · {f.maxCapacity} places
                    </div>
                  </div>
                  <button
                    onClick={() => startEdit(f)}
                    className="text-xs font-semibold text-ink-700 hover:text-brand-500"
                  >
                    modifier
                  </button>
                  <button
                    onClick={() => handleDelete(f)}
                    className="text-xs font-semibold text-brand-500 hover:underline"
                    disabled={remove.isPending}
                  >
                    supprimer
                  </button>
                </div>
              );
            })}
          </div>

          {/* Formulaire ajout / édition */}
          <div className="mt-5 border-t border-ink-200 pt-4">
            <h3 className="text-sm font-semibold text-ink-700">
              {isEditing ? "Modifier le type" : "Ajouter un type"}
            </h3>
            <div className="mt-3 grid gap-3 sm:grid-cols-2">
              <label className="block">
                <span className="text-xs font-semibold uppercase tracking-wider text-ink-500">
                  Code (identifiant interne)
                </span>
                <input
                  className="input mt-1"
                  value={draft.code}
                  placeholder="WOD"
                  onChange={(e) => setDraft({ ...draft, code: e.target.value })}
                />
              </label>
              <label className="block">
                <span className="text-xs font-semibold uppercase tracking-wider text-ink-500">
                  Nom affiché
                </span>
                <input
                  className="input mt-1"
                  value={draft.name}
                  placeholder="WOD"
                  onChange={(e) => setDraft({ ...draft, name: e.target.value })}
                />
              </label>
              <label className="block">
                <span className="text-xs font-semibold uppercase tracking-wider text-ink-500">
                  Durée (minutes)
                </span>
                <input
                  className="input mt-1"
                  type="number"
                  min={1}
                  value={draft.duration}
                  onChange={(e) => setDraft({ ...draft, duration: Number(e.target.value) })}
                />
              </label>
              <label className="block">
                <span className="text-xs font-semibold uppercase tracking-wider text-ink-500">
                  Capacité max
                </span>
                <input
                  className="input mt-1"
                  type="number"
                  min={1}
                  value={draft.maxCapacity}
                  onChange={(e) => setDraft({ ...draft, maxCapacity: Number(e.target.value) })}
                />
              </label>
              <label className="block sm:col-span-2">
                <span className="text-xs font-semibold uppercase tracking-wider text-ink-500">
                  Description
                </span>
                <textarea
                  className="input mt-1"
                  rows={2}
                  value={draft.description}
                  onChange={(e) => setDraft({ ...draft, description: e.target.value })}
                />
              </label>
              <label className="block">
                <span className="text-xs font-semibold uppercase tracking-wider text-ink-500">
                  Couleur
                </span>
                <div className="mt-1 flex items-center gap-2">
                  <input
                    type="color"
                    value={colorValid ? draft.color : "#DC2626"}
                    onChange={(e) => setDraft({ ...draft, color: e.target.value })}
                    className="h-9 w-12 shrink-0 cursor-pointer rounded border border-ink-200"
                  />
                  <input
                    className="input"
                    value={draft.color}
                    placeholder="#DC2626"
                    onChange={(e) => setDraft({ ...draft, color: e.target.value })}
                  />
                </div>
                {!colorValid && (
                  <p className="mt-1 text-xs text-brand-500">Format attendu : #RRGGBB</p>
                )}
              </label>
            </div>

            <div className="mt-4 flex gap-2">
              <button
                onClick={() => save.mutate()}
                disabled={!canSave || save.isPending}
                className="btn-primary disabled:opacity-50"
              >
                {save.isPending ? "…" : isEditing ? "Enregistrer" : "Ajouter"}
              </button>
              {isEditing && (
                <button onClick={resetForm} className="btn-outline" type="button">
                  Annuler
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
